/**
 * tokenService.ts
 *
 * Централізоване сховище access/refresh токенів.
 *
 * Зберігання: пам'ять модуля + sessionStorage як резерв.
 * sessionStorage живе поки відкрита вкладка — при закритті скидається.
 * Це дозволяє пережити hard reload в рамках однієї сесії (вкладки).
 *
 * При першому виклику будь-якої функції — автоматично відновлює
 * токени з sessionStorage якщо пам'ять порожня.
 */

const SESSION_KEY = 'qt_tokens';

interface TokenData {
    accessToken:            string;
    refreshToken:           string;
    accessTokenExpiresIn:   number;  // секунди
    storedAt:               number;  // Date.now() в момент збереження
}

// ── Module-level сховище ──────────────────────────────────────────────────────
let tokenData: TokenData | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

// ── sessionStorage helpers ────────────────────────────────────────────────────

const saveToSession = (data: TokenData): void => {
    try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
    } catch {
        // sessionStorage недоступний (приватний режим і т.д.) — ігноруємо
    }
};

const loadFromSession = (): TokenData | null => {
    try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const removeFromSession = (): void => {
    try {
        sessionStorage.removeItem(SESSION_KEY);
    } catch { /* ignore */ }
};

/**
 * Відновлює токени з sessionStorage якщо пам'ять порожня.
 * Викликається автоматично перед кожною операцією.
 */
const restoreIfNeeded = (): void => {
    if (tokenData) return;
    const saved = loadFromSession();
    if (saved) {
        tokenData = saved;
    }
};

// ── Публічне API ──────────────────────────────────────────────────────────────

export const setTokens = (data: {
    access_token:            string;
    refresh_token:           string;
    access_token_expires_in: number;
}): void => {
    tokenData = {
        accessToken:          data.access_token,
        refreshToken:         data.refresh_token,
        accessTokenExpiresIn: data.access_token_expires_in,
        storedAt:             Date.now(),
    };
    // Зберігаємо в sessionStorage — переживе hard reload в рамках вкладки
    saveToSession(tokenData);
};

export const getAccessToken = (): string | null => {
    restoreIfNeeded();
    return tokenData?.accessToken ?? null;
};

export const getRefreshToken = (): string | null => {
    restoreIfNeeded();
    return tokenData?.refreshToken ?? null;
};

/**
 * Перевіряє чи access_token ще дійсний (з 30-секундним буфером).
 */
export const isTokenValid = (): boolean => {
    restoreIfNeeded();
    if (!tokenData) return false;
    const elapsedMs = Date.now() - tokenData.storedAt;
    const ttlMs     = tokenData.accessTokenExpiresIn * 1000;
    return elapsedMs < ttlMs - 30_000;
};

export const clearTokens = (): void => {
    tokenData = null;
    removeFromSession();
    if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
    }
};

export const scheduleRefresh = (onRefresh: () => Promise<void>): void => {
    restoreIfNeeded();
    if (!tokenData) return;
    if (refreshTimer) clearTimeout(refreshTimer);

    const refreshInMs = tokenData.accessTokenExpiresIn * 1000 - 60_000;

    if (refreshInMs <= 0) {
        onRefresh().catch(clearTokens);
        return;
    }

    refreshTimer = setTimeout(() => {
        onRefresh().catch(clearTokens);
    }, refreshInMs);
};